import type {
  DraftFieldMapping,
  RelationshipMapping,
  RoleMapping,
  WritebackFieldMapping,
} from "../../../hubspot/new-flow/types";

export interface FieldReservationEntry {
  fieldKey: string;
  owner: string;
}

export interface FieldConflict {
  fieldKey: string;
  owners: string[];
}

export interface ObjectReservationState {
  reservedFields: Set<string>;
  conflicts: FieldConflict[];
  ownerByField: Record<string, string[]>;
}

export interface ObjectValidationError {
  object: string;
  section: "matcher" | "field_mappings" | "relationships" | "writeback" | "conflicts";
  message: string;
}

interface BuildReservationParams {
  matcherFieldName?: string;
  mappings: DraftFieldMapping[];
  roleMappings: RoleMapping[];
  relationshipMappings: RelationshipMapping[];
  writebackMappings: WritebackFieldMapping[];
}

const normalizeField = (value?: string | null): string => String(value || "").trim();

const addReservation = (
  entries: FieldReservationEntry[],
  fieldKey: string | undefined,
  owner: string,
) => {
  const normalized = normalizeField(fieldKey);
  if (!normalized) return;
  entries.push({ fieldKey: normalized, owner });
};

export const buildObjectReservations = ({
  matcherFieldName,
  mappings,
  roleMappings,
  relationshipMappings,
  writebackMappings,
}: BuildReservationParams): ObjectReservationState => {
  const entries: FieldReservationEntry[] = [];

  addReservation(entries, matcherFieldName, "matcher");

  mappings.forEach((mapping, index) => {
    addReservation(entries, mapping.source_field_name, `trait:${index}`);
  });

  roleMappings.forEach((mapping, index) => {
    addReservation(entries, mapping.hubspot_owner_field, `role:${index}`);
  });

  relationshipMappings.forEach((mapping, index) => {
    addReservation(entries, mapping.hubspot_list_field, `relationship:${index}`);
  });

  writebackMappings.forEach((mapping, index) => {
    addReservation(entries, mapping.hubspot_field, `writeback:${index}`);
  });

  const ownerByField = entries.reduce<Record<string, string[]>>((acc, entry) => {
    if (!acc[entry.fieldKey]) {
      acc[entry.fieldKey] = [];
    }
    acc[entry.fieldKey].push(entry.owner);
    return acc;
  }, {});

  const conflicts = Object.entries(ownerByField)
    .filter(([, owners]) => owners.length > 1)
    .map(([fieldKey, owners]) => ({ fieldKey, owners }));

  return {
    reservedFields: new Set(Object.keys(ownerByField)),
    conflicts,
    ownerByField,
  };
};

interface ValidateObjectDraftParams {
  object: string;
  enabled: boolean;
  matcherFieldName?: string;
  conflicts: FieldConflict[];
  enabledObjects: string[];
  relationshipMappings: RelationshipMapping[];
  writebackEnabled: boolean;
  writebackMappings: WritebackFieldMapping[];
}

export const validateObjectDraft = ({
  object,
  enabled,
  matcherFieldName,
  conflicts,
  enabledObjects,
  relationshipMappings,
  writebackEnabled,
  writebackMappings,
}: ValidateObjectDraftParams): ObjectValidationError[] => {
  if (!enabled) return [];

  const errors: ObjectValidationError[] = [];

  if (!normalizeField(matcherFieldName)) {
    errors.push({
      object,
      section: "matcher",
      message: "External ID matcher field is required.",
    });
  }

  if (conflicts.length > 0) {
    errors.push({
      object,
      section: "conflicts",
      message: `Duplicate provider fields found: ${conflicts.map((conflict) => conflict.fieldKey).join(", ")}`,
    });
  }

  if (object === "contacts") {
    const enabledSet = new Set(enabledObjects);
    relationshipMappings.forEach((mapping) => {
      if (!mapping.target_object || !enabledSet.has(mapping.target_object)) {
        errors.push({
          object,
          section: "relationships",
          message: `Relationship target "${mapping.target_object}" is not enabled.`,
        });
      }
    });
  }

  if (writebackEnabled) {
    const validWritebackMappings = writebackMappings.filter((mapping) => (
      normalizeField(mapping.hyperengage_field) && normalizeField(mapping.hubspot_field)
    ));

    if (validWritebackMappings.length === 0) {
      errors.push({
        object,
        section: "writeback",
        message: "Writeback is enabled but no valid writeback mappings were configured.",
      });
    }
  }

  return errors;
};
